import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { frame, cancelFrame } from 'framer-motion';
import { isInPageTransition, isLpPath } from '../../lib/routes';

// Lenisとframer-motionのrAFループ競合を避けるため、
// autoRaf を切って framer-motion の frame.update で駆動する
export default function SmoothScroll() {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);
  // 広告の着地ページ（/lp/...）は、ブラウザのふつうのスクロールにする（サイトの装飾から切り離す）
  const lp = isLpPath(pathname);

  useEffect(() => {
    if (lp || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      autoRaf: false,
    });
    window.__lenis = lenis;
    const update = (data) => lenis.raf(data.timestamp);
    frame.update(update, true);
    return () => {
      cancelFrame(update);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, [lp]);

  useEffect(() => {
    const prev = prevPath.current;
    prevPath.current = pathname;
    // メンバーモーダル・採用サイトのタブはURLだけ変わるページ内操作なので、トップへ戻さない
    if (prev !== pathname && isInPageTransition(prev, pathname)) return;
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname]);

  return null;
}

// 固定ヘッダー（コーポレート・採用サイトとも）の下端。行き先の見出しがヘッダーに隠れないようにずらす量。
// ヘッダーの高さはスマホ64px・PC72px。下の罫線1px分は重ねる。
function headerOffset() {
  for (const h of document.querySelectorAll('header')) {
    const pos = getComputedStyle(h).position;
    if (pos === 'fixed' || pos === 'sticky') return Math.max(0, h.getBoundingClientRect().bottom - 1);
  }
  return 71;
}

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  // 行き先は数値で渡す。Lenis に要素を渡すと html の scroll-padding-top（index.css）も
  // 差し引かれ、ヘッダー分のずらしと二重になるため。
  const top = Math.round(el.getBoundingClientRect().top + window.scrollY - headerOffset());
  if (window.__lenis) {
    // Lenis はページの高さを 250ms 遅れて測り直し、その高さで行き先を切り詰める。短いページ（募集ごとのページなど）から
    // /careers/positions へ移った直後は前のページの高さのままなので、先に測り直させる
    window.__lenis.resize();
    window.__lenis.scrollTo(top, { duration: 1.3 });
  } else {
    // 動きを減らす設定のときは Lenis が無いので、ブラウザのスクロールで送る
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
  }
}
